/**
 * p7-performance.js
 * Performance7 Automotive Theme Enhancements for Shopify Horizon
 *
 * Features:
 *   1. Scroll-reveal via IntersectionObserver ([data-p7-reveal])
 *   2. Auto-tagging of section headings with data-p7-reveal
 *   3. Staggered sibling animation delays (data-p7-reveal-delay)
 *   4. Counter animation for [data-p7-count] elements
 *   5. Page-load fade-in (adds .p7-page-loaded to body)
 *
 * Everything lives inside a self-executing function to keep
 * the global namespace clean.
 */

(function p7Performance() {
  'use strict';

  // ------------------------------------------------------------------
  // CONSTANTS
  // ------------------------------------------------------------------

  /** Threshold: how much of the element must be visible before revealing */
  const REVEAL_THRESHOLD = 0.1;

  /**
   * rootMargin: negative bottom margin shrinks the "active zone",
   * so elements trigger slightly before reaching the very bottom edge.
   */
  const REVEAL_ROOT_MARGIN = '0px 0px -60px 0px';

  /** CSS class added to an element once it has been revealed */
  const REVEALED_CLASS = 'p7-revealed';

  /**
   * Selectors for elements that should be auto-tagged with
   * [data-p7-reveal] if an editor has not already added it manually.
   * Targets headings and lead paragraphs inside common Horizon
   * section wrappers.
   */
  const AUTO_REVEAL_SELECTORS = [
    '.section h2',
    '.section h3',
    '.section-header h2',
    '.section-header h3',
    '.section-header__title',
    'main h2',
    'main h3',
    '.shopify-section h2',
    '.shopify-section h3',
    '.banner__heading',
    '.hero__heading',
    '.featured-collection h2',
    '.rich-text__heading',
    '.image-with-text__heading',
    '.multicolumn-card__title',
    '.collage-card__heading',
  ];

  /**
   * Default stagger increment (ms) applied between consecutive
   * siblings that share the same parent and are all marked for reveal.
   */
  const STAGGER_INCREMENT_MS = 80;

  /** Counter animation duration in milliseconds */
  const COUNTER_DURATION_MS = 1800;

  /** Easing function used by the counter (ease-out cubic) */
  function easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }


  // ------------------------------------------------------------------
  // 1. PAGE-LOAD TRANSITION
  // ------------------------------------------------------------------
  // CSS holds `body { opacity: 0 }` until this class is applied.
  // Adding it on DOMContentLoaded gives a clean fade-in.

  function initPageTransition() {
    document.body.classList.add('p7-page-loaded');
  }


  // ------------------------------------------------------------------
  // 2. AUTO-TAG SECTION HEADINGS WITH data-p7-reveal
  // ------------------------------------------------------------------
  // Editors can manually add data-p7-reveal to any element.
  // This function adds it automatically to common heading selectors
  // inside Horizon section wrappers so the theme works out of the box.

  function autoTagRevealElements() {
    const selector = AUTO_REVEAL_SELECTORS.join(', ');
    const candidates = document.querySelectorAll(selector);

    candidates.forEach(function (el) {
      // Skip if the element or any ancestor already has the attribute
      if (el.closest('[data-p7-reveal]')) return;

      // Default reveal direction: up (the CSS default)
      el.setAttribute('data-p7-reveal', '');
    });
  }


  // ------------------------------------------------------------------
  // 3. APPLY STAGGERED DELAYS TO SIBLING GROUPS
  // ------------------------------------------------------------------
  // For each parent that has multiple [data-p7-reveal] children,
  // assign incremental animation-delay values so siblings cascade in.
  //
  // An element can opt out of auto-stagger by setting
  // data-p7-reveal-delay to a specific ms value; that value is
  // respected verbatim.

  function applyStaggerDelays() {
    // Group all reveal elements by their parent
    const allReveal = document.querySelectorAll('[data-p7-reveal]');
    const parentMap = new Map();

    allReveal.forEach(function (el) {
      const parent = el.parentElement;
      if (!parent) return;
      if (!parentMap.has(parent)) {
        parentMap.set(parent, []);
      }
      parentMap.get(parent).push(el);
    });

    // For parents with more than one reveal child, apply stagger
    parentMap.forEach(function (children) {
      if (children.length < 2) return;

      children.forEach(function (child, index) {
        // Honour manually set delay; otherwise auto-calculate
        if (child.hasAttribute('data-p7-reveal-delay')) {
          const manualDelay = parseInt(child.getAttribute('data-p7-reveal-delay'), 10);
          if (!isNaN(manualDelay)) {
            child.style.transitionDelay = manualDelay + 'ms';
          }
        } else {
          child.style.transitionDelay = (index * STAGGER_INCREMENT_MS) + 'ms';
        }
      });
    });
  }


  // ------------------------------------------------------------------
  // 4. SCROLL-REVEAL via IntersectionObserver
  // ------------------------------------------------------------------
  // Observes all [data-p7-reveal] elements. When an element crosses
  // the viewport threshold, the REVEALED_CLASS is added and the
  // element is unobserved (one-shot reveal, not re-hiding on scroll-up).

  function initScrollReveal() {
    // Graceful degradation: if IntersectionObserver is not available
    // (very old browsers), reveal everything immediately.
    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll('[data-p7-reveal]').forEach(function (el) {
        el.classList.add(REVEALED_CLASS);
      });
      return;
    }

    const observer = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;

          entry.target.classList.add(REVEALED_CLASS);

          // Stop watching — we only reveal once
          obs.unobserve(entry.target);
        });
      },
      {
        threshold: REVEAL_THRESHOLD,
        rootMargin: REVEAL_ROOT_MARGIN,
      }
    );

    // Observe all current reveal elements
    document.querySelectorAll('[data-p7-reveal]').forEach(function (el) {
      observer.observe(el);
    });

    // Also observe any elements added dynamically (e.g. by Horizon's
    // section hydration or infinite scroll).
    watchForNewRevealElements(observer);
  }


  // ------------------------------------------------------------------
  // 5. MUTATION OBSERVER — watch for dynamically added reveal elements
  // ------------------------------------------------------------------
  // Horizon can inject sections after initial load (section hydration,
  // quick-add drawers, etc.). We watch the document for new nodes and
  // attach the scroll-reveal observer to any that need it.

  function watchForNewRevealElements(revealObserver) {
    if (!('MutationObserver' in window)) return;

    const mutationObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== Node.ELEMENT_NODE) return;

          // Check if the node itself needs revealing
          if (node.hasAttribute && node.hasAttribute('data-p7-reveal')) {
            revealObserver.observe(node);
          }

          // Check descendants
          node.querySelectorAll &&
            node.querySelectorAll('[data-p7-reveal]').forEach(function (el) {
              if (!el.classList.contains(REVEALED_CLASS)) {
                revealObserver.observe(el);
              }
            });
        });
      });
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }


  // ------------------------------------------------------------------
  // 6. COUNTER ANIMATION
  // ------------------------------------------------------------------
  // Elements with [data-p7-count="<number>"] will count up from 0 to
  // the given number when scrolled into view.
  //
  // Optional attributes:
  //   data-p7-count-prefix="$"   — prepend a symbol (e.g. currency)
  //   data-p7-count-suffix="%"   — append a symbol
  //   data-p7-count-separator="," — thousands separator (default: none)
  //
  // Example: <span data-p7-count="2500" data-p7-count-suffix="+">0</span>

  function initCounters() {
    if (!('IntersectionObserver' in window)) return;

    const counters = document.querySelectorAll('[data-p7-count]');
    if (!counters.length) return;

    const counterObserver = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          obs.unobserve(entry.target);
          animateCounter(entry.target);
        });
      },
      {
        threshold: 0.25,
      }
    );

    counters.forEach(function (el) {
      counterObserver.observe(el);
    });
  }

  /**
   * Runs a requestAnimationFrame loop to count an element from 0
   * to its [data-p7-count] target value.
   *
   * @param {HTMLElement} el
   */
  function animateCounter(el) {
    const target     = parseFloat(el.getAttribute('data-p7-count')) || 0;
    const prefix     = el.getAttribute('data-p7-count-prefix') || '';
    const suffix     = el.getAttribute('data-p7-count-suffix') || '';
    const separator  = el.getAttribute('data-p7-count-separator') || '';
    const isFloat    = target % 1 !== 0;
    const decimals   = isFloat ? (String(target).split('.')[1] || '').length : 0;

    let startTime = null;

    function formatNumber(value) {
      let formatted = isFloat
        ? value.toFixed(decimals)
        : Math.floor(value).toString();

      // Apply thousands separator if requested
      if (separator) {
        const parts = formatted.split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, separator);
        formatted = parts.join('.');
      }

      return prefix + formatted + suffix;
    }

    function step(timestamp) {
      if (!startTime) startTime = timestamp;

      const elapsed  = timestamp - startTime;
      const progress = Math.min(elapsed / COUNTER_DURATION_MS, 1);
      const eased    = easeOutCubic(progress);
      const current  = eased * target;

      el.textContent = formatNumber(current);

      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        // Ensure we land exactly on the target
        el.textContent = formatNumber(target);
      }
    }

    requestAnimationFrame(step);
  }


  // ------------------------------------------------------------------
  // 7. HEADER SCROLL CLASS
  // ------------------------------------------------------------------
  // Adds .scrolled to the header when the user scrolls down, which
  // the CSS uses to slightly increase header opacity.

  function initHeaderScroll() {
    const header = document.querySelector(
      '.site-header, header-component, .header'
    );
    if (!header) return;

    let ticking = false;

    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(function () {
          header.classList.toggle('scrolled', window.scrollY > 20);
          ticking = false;
        });
        ticking = true;
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
  }


  // ------------------------------------------------------------------
  // INIT — wire everything up on DOMContentLoaded
  // ------------------------------------------------------------------

  function init() {
    // 1. Trigger page fade-in
    initPageTransition();

    // 2. Auto-tag headings that don't already have data-p7-reveal
    autoTagRevealElements();

    // 3. Apply stagger delays to sibling groups
    applyStaggerDelays();

    // 4. Start scroll-reveal observer
    initScrollReveal();

    // 5. Start counter observers
    initCounters();

    // 6. Header scroll-state class
    initHeaderScroll();
  }

  // Run after the DOM is parsed (works whether script is deferred or not)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // DOM is already ready (e.g. script loaded async after parse)
    init();
  }

})(); // end self-executing function
